import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Tooltip, keyframes } from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import { pavoApiService } from '../../../../services/pavoApiService';
import { pavoSignalRService } from '../../../../services/pavoSignalRService';

export interface PavoLiveBadgeProps {
  /** DeviceScreen.ScreenCode (pavo_rest_p2p cihazı). */
  deviceCode: string | null | undefined;
  /** Opsiyonel cihaz adı gösterimi. */
  deviceName?: string | null;
  /** Badge boyutu. */
  size?: 'small' | 'medium' | 'large';
  /** Cihaz adını gizle (sadece PAVO rozeti). */
  hideDeviceName?: boolean;
  /** Online/offline değişimlerini dışa bildir. */
  onOnlineChange?: (isOnline: boolean) => void;
  /**
   * Görünüm varyantı:
   * - `full` (varsayılan): Router ikonu + "PAVO CANLI/OFFLINE" yazısı + cihaz adı.
   * - `dot`: Sadece pulsing yeşil/kırmızı nokta (yazısız). Button içine gömmek için.
   */
  variant?: 'full' | 'dot';
}

const pulseGreen = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.55); }
  70% { box-shadow: 0 0 0 12px rgba(46, 125, 50, 0); }
  100% { box-shadow: 0 0 0 0 rgba(46, 125, 50, 0); }
`;

/**
 * Pavo Rest-P2P cihazının online/offline durumunu gösteren canlı rozet.
 * - Mount'ta REST ile cihaz durumunu çeker.
 * - SignalR `DeviceConnected/Disconnected` event'lerini dinler.
 */
const PavoLiveBadge: React.FC<PavoLiveBadgeProps> = ({
  deviceCode,
  deviceName,
  size = 'medium',
  hideDeviceName = false,
  onOnlineChange,
  variant = 'full',
}) => {
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [resolvedName, setResolvedName] = useState<string | null>(deviceName ?? null);
  const subIdRef = useRef<string>(`pavo-live-${Math.random().toString(36).slice(2)}`);
  const onOnlineChangeRef = useRef(onOnlineChange);
  onOnlineChangeRef.current = onOnlineChange;

  useEffect(() => {
    setResolvedName(deviceName ?? null);
  }, [deviceName]);

  useEffect(() => {
    if (!deviceCode) {
      setIsOnline(false);
      setLoading(false);
      return;
    }

    let disposed = false;
    setLoading(true);

    (async () => {
      try {
        const device = await pavoApiService.getPavoDevice(deviceCode);
        if (disposed) return;
        setIsOnline(Boolean(device.isOnline));
        onOnlineChangeRef.current?.(Boolean(device.isOnline));
        if (!deviceName && device.deviceName) {
          setResolvedName(device.deviceName);
        }
      } catch (err) {
        console.warn('[PavoLiveBadge] Cihaz durumu alınamadı:', err);
        if (!disposed) setIsOnline(false);
      } finally {
        if (!disposed) setLoading(false);
      }
    })();

    // SignalR status callback'i global listede — kendimize özel bir dinleyici eklemek için
    // pavoSignalRService'teki genel device-status callback mekanizmasını kullanıyoruz.
    const deviceStatusHandler = (code: string, online: boolean) => {
      if (code === deviceCode) {
        setIsOnline(online);
        onOnlineChangeRef.current?.(online);
      }
    };
    pavoSignalRService.onDeviceStatus(deviceStatusHandler);

    return () => {
      disposed = true;
      // pavoSignalRService.onDeviceStatus bir listeye eklendiği için burada silmek
      // kolayca mümkün değil (mevcut implementasyon). Side-effect yalnızca state
      // güncellemesi — component unmount sonrası setState'i guard'lıyoruz.
    };
  }, [deviceCode, deviceName]);

  const sizes = {
    small: { px: 1.2, py: 0.4, fontSize: 12, iconSize: 14, dot: 8 },
    medium: { px: 1.6, py: 0.6, fontSize: 13, iconSize: 16, dot: 10 },
    large: { px: 2, py: 0.8, fontSize: 15, iconSize: 18, dot: 12 },
  }[size];

  // ─────────────────────────────────────────────────────────
  // DOT VARIANT — sadece küçük pulsing nokta (button içine gömülür)
  // ─────────────────────────────────────────────────────────
  if (variant === 'dot') {
    const dotSize = size === 'large' ? 14 : size === 'small' ? 9 : 11;
    const dotColor = !deviceCode || loading
      ? '#bdbdbd'
      : isOnline ? '#2e7d32' : '#c62828';
    const dotTooltip = !deviceCode
      ? 'Rest-P2P cihazı seçilmemiş'
      : loading
        ? 'Pavo durumu kontrol ediliyor...'
        : isOnline
          ? `Pavo cihazı çevrimiçi${resolvedName ? ` — ${resolvedName}` : ''}`
          : `Pavo cihazı çevrimdışı${resolvedName ? ` — ${resolvedName}` : ''}`;

    return (
      <Tooltip title={dotTooltip} arrow>
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            bgcolor: dotColor,
            border: '1.5px solid rgba(255,255,255,0.9)',
            boxShadow: isOnline && !loading
              ? '0 0 0 2px rgba(46,125,50,0.25)'
              : '0 0 0 1px rgba(0,0,0,0.15)',
            flex: 'none',
            animation: isOnline && !loading ? `${pulseGreen} 2.2s ease-out infinite` : 'none',
          }}
        />
      </Tooltip>
    );
  }

  const color = !deviceCode
    ? { bg: '#f5f5f5', fg: '#9e9e9e', border: '#e0e0e0' }
    : loading
      ? { bg: '#f5f5f5', fg: '#757575', border: '#e0e0e0' }
      : isOnline
        ? { bg: '#e8f5e9', fg: '#2e7d32', border: '#81c784' }
        : { bg: '#ffebee', fg: '#c62828', border: '#ef9a9a' };

  const label = !deviceCode
    ? 'PAVO YOK'
    : loading
      ? 'PAVO...'
      : isOnline
        ? 'PAVO CANLI'
        : 'PAVO OFFLINE';

  const tooltip = !deviceCode
    ? 'Rest-P2P cihazı seçilmemiş'
    : isOnline
      ? `Pavo cihazı çevrimiçi${resolvedName ? ` — ${resolvedName}` : ''}`
      : `Pavo cihazı çevrimdışı${resolvedName ? ` — ${resolvedName}` : ''}`;

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.8,
          px: sizes.px,
          py: sizes.py,
          borderRadius: 999,
          bgcolor: color.bg,
          color: color.fg,
          border: `1px solid ${color.border}`,
          fontWeight: 700,
          fontSize: sizes.fontSize,
          lineHeight: 1,
          userSelect: 'none',
          animation: isOnline && !loading ? `${pulseGreen} 2.2s ease-out infinite` : 'none',
        }}
      >
        <RouterIcon sx={{ fontSize: sizes.iconSize }} />
        <Box
          component="span"
          sx={{
            width: sizes.dot,
            height: sizes.dot,
            borderRadius: '50%',
            bgcolor: isOnline && !loading ? '#2e7d32' : !deviceCode || loading ? '#bdbdbd' : '#c62828',
            flex: 'none',
            boxShadow: isOnline && !loading ? '0 0 0 2px rgba(46,125,50,0.18)' : 'none',
          }}
        />
        <Typography component="span" sx={{ fontWeight: 800, fontSize: sizes.fontSize, lineHeight: 1 }}>
          {label}
        </Typography>
        {!hideDeviceName && resolvedName && (
          <Typography component="span" sx={{ fontWeight: 500, fontSize: sizes.fontSize - 1, opacity: 0.85 }}>
            • {resolvedName}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default PavoLiveBadge;
